import React, { useEffect, useState } from "react";

import { Box, IconButton, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { OpenInNew } from "@mui/icons-material";
import { useTheme, lighten } from "@mui/material/styles";

import { AudienceMiniStats } from "@/components/molecules";
import { AudienceSegment, AudienceSummary } from "@/types";
import { toIconLabel } from "@/utils";

interface SegmentPanelProps {
  segment: AudienceSegment;
  segmentValue: string;
  segmentSize?: AudienceSummary;
  description: string;
  align: "left" | "right";
  color: string
  onSelect?: (segment: AudienceSegment) => void;
}

export const SegmentPanel: React.FC<SegmentPanelProps> = ({
  segment,
  segmentValue,
  segmentSize,
  description,
  align,
  color,
  onSelect
}) => {

  const [src, setSrc] = useState<string>(`https://sc-opendata.s3.eu-west-1.amazonaws.com/mindset_images/${toIconLabel(segment.segment_name)}.png`);
  const defaultImage = "https://sc-opendata.s3.eu-west-1.amazonaws.com/mindset_images/default.png";

  const theme = useTheme();

  useEffect(() => {
    setSrc(`https://sc-opendata.s3.eu-west-1.amazonaws.com/mindset_images/${toIconLabel(segment.segment_name)}.png`)
  }, [segment])

  const handleSegmentClick = () => {
    onSelect && onSelect(segment);
  }

  return (
    <>
      {/* Small Screen */}
      <Stack
        direction="row"
        spacing={2}
        p={2}
        alignItems="stretch"
        sx={{
          display: { "sm": "flex", "lg": "none" },
          background: `linear-gradient(125deg, ${color} 0%, ${lighten(theme.palette.background.paper, 0.051)} 80%)`,
          borderRadius: 2
        }}
      >
        <Box display="flex" alignSelf="stretch">
          <Box
            component="img"
            src={src}
            alt={segment.segment_name}
            height="100%"
            minHeight={200}
            width={300}
            sx={{ borderRadius: 2, objectFit: "cover" }}
            onError={() => setSrc(defaultImage)}
          />
        </Box>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{`${segment.segment_name} (${segmentValue})`}</Typography>
              { onSelect &&
                <IconButton onClick={handleSegmentClick}>
                  <OpenInNew />
                </IconButton>
              }
            </Box>
            {segmentSize &&
              <AudienceMiniStats audienceSize={segmentSize} size="small"/>
            }
          </Stack>
          <Box height="100%" display="flex" alignItems="center">
            <Typography variant="body2">
              {description}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Large Screen */}
      <Stack
        direction="row"
        spacing={2}
        p={2}
        alignItems="stretch"
        sx={{
          display: { "sm": "none", "lg": "flex" },
          background: `linear-gradient(125deg, ${color} 0%, ${lighten(theme.palette.background.paper, 0.051)} 80%)`,
          borderRadius: 2
        }}
      >
        <Box display="flex" alignSelf="stretch">
          <Box
            component="img"
            src={src}
            alt={segment.segment_name}
            height="100%"
            minHeight={200}
            width={300}
            sx={{ borderRadius: 2, objectFit: "cover" }}
            onError={() => setSrc(defaultImage)}
          />
        </Box>
        <Stack spacing={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography>{`${segment.segment_name} (${segmentValue})`}</Typography>
            <Stack direction="row" spacing={1}>
              {segmentSize &&
                <AudienceMiniStats audienceSize={segmentSize} size="small"/>
              }
              { onSelect &&
                <IconButton onClick={handleSegmentClick}>
                  <OpenInNew />
                </IconButton>
              }
            </Stack>
          </Box>
          <Box height="100%" display="flex" alignItems="center">
            <Typography variant="body2">
              {description}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default SegmentPanel;
